import {
  CURRENCIES,
  OPTIONS_ARCHIVED_LIST,
  OPTIONS_LIST,
} from '../constants/lists';
import { Currency } from '../types/common';
import { camelCase, startCase } from 'lodash';
import { Category } from '../components/NewStrategy/types';
import { StrategyAssetsParentGroup, Target } from '../components/Widget/types';
import moment from 'moment';
import { COLORS_HEX } from '../constants/colors';
import { t } from 'i18next';
import { INTERVALS } from '../hooks/useIntervals';
import {
  IStrategy,
  IStrategyAsset,
  IStrategyAssetTarget,
  TSeries,
  TTickerCurrency,
} from '../types/model';
import { WidgetWithPieModel } from '../components/Widget/WidgetWithPie/WidgetWithPie.model';
import { Event, LineChartData } from '../components/Strategies/types';
import { TDateISODate } from '../types/date';
import { NumberAbbreviationEnum, CurrencyCodeEnum } from '../constants/enums';

export const formatCurrency = (
  n: number,
  currency: CurrencyCodeEnum = CurrencyCodeEnum.USD
) => {
  const CurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  return CurrencyFormatter.format(n);
};

export const formatNumber = (n: number) => {
  const CurrencyFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  return CurrencyFormatter.format(n);
};

export const formatDateTime = (date: string | Date) => {
  if (typeof date === 'string') {
    return formatDate(date) + ' ' + date.split('T')[1].substring(0, 5);
  } else {
    return (
      formatDate(date) + ' ' + date.toISOString().split('T')[1].substring(0, 5)
    );
  }
};

export const isSameDay = (startDate: string | Date, endDate: string | Date) => {
  return formatDate(startDate, true) === formatDate(endDate, true);
};

export const dateUTCToLocal = (date: Date) => {
  const tzOffset = new Date().getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset);
};

export const formatTime = (date: string | Date, local: boolean = false) => {
  const dt = typeof date === 'string' ? new Date(date) : date;

  return (local ? dateUTCToLocal(dt) : dt)
    .toISOString()
    .split('T')[1]
    .substring(0, 5);
};

export const formatDate = (date: string | Date, local: boolean = false) => {
  const dt = typeof date === 'string' ? new Date(date) : date;
  const isoString = (local ? dateUTCToLocal(dt) : dt).toISOString();
  return isoString.slice(0, 10).split('-').join('/');
};

export const formatAWSDate = (date: Date): TDateISODate => {
  let day: number | string = date.getDate();
  day < 10 && (day = '0' + day);
  let month: number | string = date.getMonth() + 1;
  month < 10 && (month = '0' + month);
  const year = date.getFullYear();

  return `${year}-${month}-${day}` as TDateISODate;
};

export const getOptionsList = (archived?: boolean) => {
  return archived ? OPTIONS_ARCHIVED_LIST : OPTIONS_LIST;
};

/**
 * This function calculates the ratio of a target value from a base value
 * To compute an absolute value use the third parameter with the value 100
 *
 * @param startValue 100%
 * @param targetValue x%
 * @param absolute 0 or 100, default 0
 * @returns number
 */
export const computePercentage = (
  startValue: number,
  targetValue: number,
  absolute: 0 | 100 = 0
) => {
  const ratio = (targetValue * 100) / startValue - absolute;

  return ratio === Infinity ? 0 : +(isNaN(ratio) ? 0 : ratio).toFixed(2);
};

export const getSymbol = (currency: Currency['code']) =>
  CURRENCIES.find(({ code }) => code === currency)!.symbol;

export interface CalcAssetValuesArgs {
  inputValues: {
    from: string;
    assetId: string;
    assetType: string;
    strategyId: string;
    categoryName: string;
  };
  initialValues: {
    amount: string | number;
    entryPrice: string | number;
    percentage: string | number;
    unit: string | number;
  };
  calcStockAssetValues: any;
  calcCryptoAssetValues: any;
}

export interface CalcAssetValuesReturn {
  amount: string | number;
  entryPrice: string | number;
  percentage: string | number;
  unit: string | number;
}

export const calcAssetValues = (
  name,
  value,
  args: CalcAssetValuesArgs
): Promise<CalcAssetValuesReturn> => {
  if (value.toString().length === 0) {
    return Promise.resolve({
      amount: '',
      entryPrice: '',
      percentage: '',
      unit: '',
    });
  }

  if (args.inputValues.assetId.length === 0) {
    return Promise.reject();
  }

  const input = {
    from: name === 'entryPrice' ? 'UNIT' : args.inputValues.from.toUpperCase(),
    assetId: args.inputValues.assetId,
    assetType: args.inputValues.assetType,
    strategyId: args.inputValues.strategyId,
  };

  if (name === 'unit' || name === 'entryPrice') {
    input['entryPrice'] =
      name === 'entryPrice' ? value : args.initialValues.entryPrice || null;

    input['unit'] = name === 'unit' ? value : args.initialValues.unit || 0;
  } else if (name === 'amount') {
    input['amountBought'] = value;
  } else {
    input['percentageOfStrategy'] = value;
  }

  if (args.inputValues.categoryName === 'Stock') {
    return args
      .calcStockAssetValues({
        variables: {
          input: input,
        },
      })
      .then((response) => {
        const {
          data: {
            calcStockAssetValues: {
              amountBought,
              entryPrice,
              percentageOfStrategy,
              unit,
            },
          },
        } = response;

        return Promise.resolve({
          amount: amountBought,
          entryPrice: entryPrice,
          percentage: percentageOfStrategy,
          unit: unit,
        });
      });
  } else if (args.inputValues.categoryName === 'Crypto') {
    return args
      .calcCryptoAssetValues({
        variables: {
          input: input,
        },
      })
      .then((response) => {
        const {
          data: {
            calcCryptoAssetValues: {
              amountBought,
              entryPrice,
              percentageOfStrategy,
              unit,
            },
          },
        } = response;

        return Promise.resolve({
          amount: amountBought,
          entryPrice: entryPrice,
          percentage: percentageOfStrategy,
          unit: unit,
        });
      });
  }

  return Promise.reject();
};

export const pascalCase = (s: string) =>
  startCase(camelCase(s)).replace(/ /g, '');

export const getLogo = (type?: string | null, logo?: string | null) => {
  const defaultPath = process.env.REACT_APP_CDN + '/';
  const placeHolder = `system/assets/logos/default/${type || 'DEFAULT'}.svg`;
  return defaultPath + (logo || placeHolder);
};

/**
 *
 * @param currencyCode - Currency['code']
 * @param series: TSeries[]
 * @param purchasedAt
 * @param entryPrice
 * @param unit
 * @returns number as price
 */
export const getVariation = (
  currencyCode: Currency['code'],
  series: TSeries[], //  Omit<Serie, 'updatedAt'>[],
  purchasedAt: string,
  entryPrice: number,
  unit: number
) => {
  //remove null values from array
  // series = series.filter((n) => n !== null && n !== undefined);
  purchasedAt = moment(purchasedAt).format('YYYY-MM-DD');

  const currency = currencyCode.toLocaleLowerCase() as TTickerCurrency;

  const [day, week, month, year] = (
    ['day', 'week', 'month', 'year'] as const
  ).map(
    (str) => moment().subtract(1, str).format('YYYY-MM-DD') as TDateISODate
  );

  const getIdx = (n: number) => {
    n < 0 && (n = 0);

    while (!series[n] && n > 0) --n;

    return n;
  };

  // console.log({ purchasedAt, day, week, month, variations });

  return {
    VAR_24H:
      unit *
      (day.localeCompare(purchasedAt) === -1 ? 0 : series[getIdx(1)][currency]),
    VAR_7D:
      unit *
      (week.localeCompare(purchasedAt) === -1
        ? 0
        : series[getIdx(7)][currency]),
    VAR_30D:
      unit *
      (month.localeCompare(purchasedAt) === -1
        ? 0
        : series[getIdx(30)][currency]),
    VAR_1Y:
      unit *
      (year.localeCompare(purchasedAt) === -1
        ? 0
        : series[getIdx(365)][currency]),
    VAR_ALL: unit * entryPrice,
  };
};

/**
 *
 * @param assets StrategyAsset[]
 * @param currency Currency['code']
 * @param interval
 * @param groupBy
 * @returns
 */
export const aggregateGroups = ({
  assets,
  currency,
  groupBy = 'STRATEGIES',
  withUnit = false,
}: {
  assets: IStrategyAsset[];
  currency: Currency['code'];
  groupBy?: WidgetWithPieModel['groupBy'];
  withUnit?: boolean;
}) => [
  ...Object.values(
    assets
      ?.filter((item) => item)
      ?.reduce((obj, asset) => {
        const {
          bank,
          entryPrice,
          purchasedAt,
          strategyAssetCategory: { title, id },
          unit,
          wallet,
          asset: {
            currency: assetCurrency,
            exchange,
            industry,
            history,
            symbol,
            region,
            type,
          },
          inspirationSource,
        } = asset;

        const lastPrice =
          history[0]?.[currency.toLocaleLowerCase() as TTickerCurrency];

        const assetEntryValue = unit * entryPrice;
        const assetTotalValue = unit * lastPrice;

        const assetTimeFrames = getVariation(
          currency,
          history, // series
          purchasedAt,
          entryPrice,
          withUnit ? unit : 1
        );

        let key =
          groupBy === 'STRATEGIES'
            ? id
            : groupBy === 'INSPIRATION'
            ? inspirationSource
            : groupBy === 'EXCHANGE'
            ? exchange
            : groupBy === 'COUNTRY'
            ? ['CRYPTO', 'USER', 'NFT'].includes(type!)
              ? type
              : region
            : groupBy === 'INDUSTRY'
            ? industry ?? type
            : groupBy === 'CURRENCY'
            ? type === 'FOREX'
              ? symbol.split('/')[0]
              : assetCurrency
            : bank ?? wallet;
        !key && (key = 'Not specified');

        return {
          ...obj,
          [key]: obj.hasOwnProperty(key)
            ? {
                ...obj[key],
                assets: [...obj[key].assets, asset],
                entryValue: obj[key].entryValue + assetEntryValue,
                totalValue: obj[key].totalValue + assetTotalValue,
                timeFrames: Object.entries(obj[key].timeFrames).reduce(
                  (res, [resKey, resValue]) => ({
                    ...res,
                    [resKey]: resValue + assetTimeFrames[resKey],
                  }),
                  {} as Category['timeFrames']
                ),
              }
            : {
                title: groupBy === 'STRATEGIES' ? title : key,
                assets: [asset],
                currency,
                ...(groupBy === 'COUNTRY' && {
                  region:
                    region ?? ['CRYPTO', 'USER', 'NFT'].includes(key)
                      ? key
                      : 'Not specified',
                }),
                entryValue: assetEntryValue,
                totalValue: assetTotalValue,
                timeFrames: assetTimeFrames,
              },
        };
      }, {} as { [key: string]: Category })
  ),
];

/**
 *
 * @param assets StrategyAsset[]
 * @param currency Currency['code']
 * @param interval
 * @param groupBy
 * @returns
 */
export const aggregateByGroupAndAsset = ({
  assets,
  currency,
  groupBy = 'STRATEGIES',
  withUnit = false,
}: {
  assets: IStrategyAsset[];
  currency: Currency['code'];
  groupBy?: WidgetWithPieModel['groupBy'];
  withUnit?: boolean;
}): StrategyAssetsParentGroup[] => {
  // Helper function to determine the group by key based on the criteria
  function determineGroupByKey(
    groupBy: WidgetWithPieModel['groupBy'],
    strategyAsset: IStrategyAsset
  ): string {
    let key =
      groupBy === 'STRATEGIES'
        ? strategyAsset?.strategyAssetCategory?.id
        : groupBy === 'INSPIRATION'
        ? strategyAsset?.inspirationSource
        : groupBy === 'EXCHANGE'
        ? strategyAsset?.asset?.exchange
        : groupBy === 'COUNTRY'
        ? ['CRYPTO', 'USER', 'NFT'].includes(strategyAsset?.asset?.type!)
          ? strategyAsset?.asset?.type
          : strategyAsset?.asset?.region
        : groupBy === 'INDUSTRY'
        ? strategyAsset?.asset?.industry ?? strategyAsset?.asset?.type
        : groupBy === 'CURRENCY'
        ? strategyAsset?.asset?.type === 'FOREX'
          ? strategyAsset?.asset?.symbol.split('/')[0]
          : strategyAsset?.asset?.currency
        : strategyAsset?.bank ?? strategyAsset?.wallet;
    !key && (key = 'Not specified');

    return key;
  }

  // Helper function to aggregate time frames
  function aggregateTimeFrames(
    currentTimeFrames: Category['timeFrames'],
    newTimeFrames: Category['timeFrames']
  ): Category['timeFrames'] {
    return Object.entries(currentTimeFrames).reduce(
      (res, [resKey, resValue]) => ({
        ...res,
        [resKey]: (resValue || 0) + (newTimeFrames[resKey] || 0), // Use || 0 to handle undefined values
      }),
      {} as Category['timeFrames']
    );
  }

  // High-level grouping by groupBy
  const groupedByCriteria = assets
    ?.filter((item) => item)
    ?.reduce((obj, strategyAsset) => {
      const lastPrice =
        strategyAsset.asset.history[0]?.[
          currency.toLocaleLowerCase() as TTickerCurrency
        ];

      const assetEntryValue = strategyAsset.unit * strategyAsset.entryPrice;
      const assetTotalValue = strategyAsset.unit * lastPrice;

      const assetTimeFrames = getVariation(
        currency,
        strategyAsset.asset.history, // series
        strategyAsset.purchasedAt,
        strategyAsset.entryPrice,
        withUnit ? strategyAsset.unit : 1
      );

      let key = determineGroupByKey(groupBy, strategyAsset);

      const title =
        groupBy === 'STRATEGIES'
          ? strategyAsset.strategyAssetCategory.title
          : key;

      // First level of grouping

      // If obj already has the key, update it, otherwise initialize it
      if (obj[key]) {
        obj[key].entryValue += assetEntryValue;
        obj[key].totalValue += assetTotalValue;
        obj[key].timeFrames = aggregateTimeFrames(
          obj[key].timeFrames,
          assetTimeFrames
        );
      } else {
        obj[key] = obj[key] || {
          title,
          currency: currency,
          strategyAssetsGroup: [],
          entryValue: assetEntryValue,
          totalValue: assetTotalValue,
          timeFrames: assetTimeFrames,
        };
      }

      // Proceed to group by assetId within each group
      const assetGroupKey = strategyAsset.assetId;
      const assetGroupTitle = strategyAsset.assetId;
      let assetGroup = obj[key].strategyAssetsGroup.find(
        (g) => g.title === assetGroupKey
      );

      if (assetGroup) {
        // Asset group exists, update it
        assetGroup.assets.push(strategyAsset);
        assetGroup.entryValue += assetEntryValue;
        assetGroup.totalValue += assetTotalValue;
        assetGroup.timeFrames = aggregateTimeFrames(
          assetGroup.timeFrames,
          assetTimeFrames
        );
      } else {
        // Asset group doesn't exist, create new one
        obj[key].strategyAssetsGroup.push({
          title: assetGroupTitle,
          assets: [strategyAsset],
          currency: currency,
          entryValue: assetEntryValue,
          totalValue: assetTotalValue,
          timeFrames: assetTimeFrames,
        });
      }

      return obj;
    }, {} as { [key: string]: StrategyAssetsParentGroup });

  // Convert the result into an array
  return Object.values(groupedByCriteria);
};

/**
 *
 * @param assets StrategyAsset[]
 * @param currency TTickerCurrency
 * @param strategyStartDate string
 * @returns  object
 */
export const getTotalValues = (
  assets: IStrategyAsset[],
  currency: TTickerCurrency,
  strategyStartDate: string
) =>
  assets.reduce(
    ({ percent, totalValue, totalEntryValue, variations }, asset) => {
      const {
        asset: { history },
        entryPrice,
        purchasedAt,
        unit,
      } = asset;

      const lastPrice = history[0]?.[currency] ?? entryPrice;
      const lastValue = unit * lastPrice;
      const entryValue = unit * entryPrice;

      const assetTimeFrames = getVariation(
        currency.toLocaleUpperCase() as Currency['code'],
        history, // series,
        purchasedAt,
        entryPrice,
        unit
      );

      Object.entries(assetTimeFrames).forEach(
        ([key, value]) => (variations[key] += value)
      );

      totalEntryValue += entryValue;
      totalValue += lastValue;
      percent = computePercentage(totalEntryValue, totalValue, 100);

      return { percent, totalValue, totalEntryValue, variations };
    },
    {
      percent: 0,
      totalValue: 0,
      totalEntryValue: 0,
      variations: {
        VAR_24H: 0,
        VAR_7D: 0,
        VAR_30D: 0,
        VAR_1Y: 0,
        VAR_ALL: 0,
      },
    }
  );

export const truncateString = (str: string, num: number) => {
  return str.length > num ? str.slice(0, num) + '...' : str;
};

export const getEndDate = () => new Date(moment().format('YYYY/MM/DD'));

const getGraphicPoints = ({
  assets,
  currency,
  endDate,
  startDate,
  strategyCreatedAt,
  useCreationDates = true,
  useUnit,
}: {
  assets: IStrategyAsset[];
  currency: TTickerCurrency;
  endDate: string; // YYYY-MM-DD
  startDate: string; // YYYY-MM-DD
  strategyCreatedAt: string; // "2022-09-27T14:40:36.976Z"
  useCreationDates: boolean;
  useUnit?: boolean;
}) => {
  // history -> date: 2022-09-30T00:00:00.190Z
  useCreationDates &&
    moment(strategyCreatedAt).format('YYYY-MM-DD').localeCompare(startDate) >
      0 &&
    (startDate = moment(strategyCreatedAt).format('YYYY-MM-DD'));
  const isValidEndDate =
    moment(strategyCreatedAt).format('YYYY-MM-DD').localeCompare(endDate) < 0;

  const graphicPoints =
    isValidEndDate || !useCreationDates
      ? [...Array(moment(endDate).diff(moment(startDate), 'days') + 1)].map(
          (_, i) => ({
            x: moment(startDate).add(i, 'days').format('YYYY-MM-DD'),
            y: 0,
          })
        )
      : [];

  if (graphicPoints.length > 0) {
    let i: number;

    assets.forEach(({ asset: { history, providerId }, purchasedAt, unit }) => {
      const startIdx =
        endDate.localeCompare(history[0].date.slice(0, 10)) > -1
          ? 0
          : history.findIndex(
              ({ date }) =>
                date
                  .slice(0, 10)
                  .localeCompare(
                    useCreationDates && endDate.localeCompare(purchasedAt) < 0
                      ? purchasedAt
                      : endDate
                  ) < 1
            );

      // if the start date does not exist in the array, the immediately following date is taken
      const endIdx = history.findIndex(
        ({ date }) =>
          (useCreationDates && startDate.localeCompare(purchasedAt) < 0
            ? purchasedAt
            : startDate
          ).localeCompare(date.slice(0, 10)) > -1
      );

      const serie = history
        .slice(startIdx, endIdx === -1 ? history.length : endIdx + 1)
        .reverse();

      let lastMatchIndex = 0;
      serie.length > 0 &&
        graphicPoints.forEach(({ x, y }, j) => {
          const matchIndex = serie.findIndex((h) => h.date.slice(0, 10) === x);

          if (matchIndex > -1) {
            lastMatchIndex = matchIndex;
          }
          graphicPoints[j].y +=
            (useUnit ? unit ?? 1 : 1) * serie[lastMatchIndex][currency];
        });
    });
  }

  // console.log('graphicPoints', graphicPoints);
  return graphicPoints;
};

/**
 * For the param id send a name
 *
 * start/end date format: YYYY-MM-DD
 *
 */
export const getDataForResponsiveLine = ({
  assets,
  color,
  endDate,
  currency,
  name: id,
  startDate,
  strategyCreatedAt,
  useUnit = false,
  useCreationDates = true,
  usePercentage = false,
}: {
  assets: IStrategyAsset[];
  color: string;
  currency: TTickerCurrency;
  endDate: string;
  name: string;
  startDate: string;
  strategyCreatedAt: string;
  useUnit?: boolean;
  useCreationDates?: boolean;
  usePercentage?: boolean;
}): LineChartData => {
  const graphicPoints = getGraphicPoints({
    assets,
    currency,
    endDate,
    startDate,
    strategyCreatedAt,
    useCreationDates,
    useUnit,
  });

  const absoluteValues = graphicPoints.map(({ y }) => y);
  const percentageValues = graphicPoints.map(({ y }) =>
    computePercentage(graphicPoints[0].y, y, 100)
  );

  const chartData = graphicPoints.map(({ x, y }, i) => ({
    x,
    y: usePercentage ? percentageValues[i] : absoluteValues[i],
  }));

  // console.log('chartData', { chartData });
  return {
    id,
    color: COLORS_HEX[color],
    data: chartData,
    absoluteValues,
    percentageValues,
  };
};

export const getElapsedTime = (startDate: Date) => {
  const endDate = new Date();

  let timeDiff = endDate.getTime() - startDate.getTime();
  timeDiff = timeDiff / 1000;
  let seconds = Math.floor(timeDiff % 60);

  timeDiff = Math.floor(timeDiff / 60);
  let minutes = timeDiff % 60;

  timeDiff = Math.floor(timeDiff / 60);
  let hours = timeDiff % 24;

  timeDiff = Math.floor(timeDiff / 24);
  let days = timeDiff;

  if (days > 0) {
    return days + t('common.daysAgo');
  } else if (hours > 0) {
    return hours + t('common.hoursAgo');
  } else if (minutes > 0) {
    return minutes + t('common.minutesAgo');
  } else if (seconds > 20) {
    return t('common.someSecondsAgo');
  } else {
    return t('common.now');
  }
};

export const getValuePercentageFromBase = (base: number, percentage: number) =>
  (base / 100) * Math.abs(percentage);

export const getStrategyAssetTargets = (targets: Target[], alert: boolean) =>
  targets.reduce(
    (arr, target) =>
      arr.concat(
        isValidTarget(target)
          ? [
              {
                alert,
                endDate: formatAWSDate(new Date(target.endDate!)),
                id: target.id,
                stopLoss: target.stopLoss.AMOUNT!,
                stopProfit: target.stopProfit.AMOUNT!,
              },
            ]
          : []
      ),
    [] as IStrategyAssetTarget[]
  );

export const isValidTarget = ({ endDate, stopLoss, stopProfit }: Target) =>
  typeof endDate !== 'undefined' &&
  stopLoss.AMOUNT &&
  stopLoss.AMOUNT > 0 &&
  stopProfit.AMOUNT &&
  stopProfit.AMOUNT > 0;

export const isElementTotallyScrolled = (element: HTMLElement) =>
  Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 1;

export const extractNumber = (str: string) => +str.replace(/[^\d.-]/g, '');

export const getAlignAssets = (map: IStrategyAsset[][]) =>
  Object.values(
    map.reduce((obj, assets, i) => {
      const unit = i === 0 ? +1 : -1;

      assets.forEach((asset) =>
        Object.hasOwn(obj, asset.assetId)
          ? (obj[asset.assetId].unit += asset.unit * unit)
          : (obj[asset.assetId] = { ...asset, unit: asset.unit * unit })
      );
      return obj;
    }, {} as { [key: string]: IStrategyAsset })
  ).reduce(
    (arr, asset) =>
      arr.concat(
        asset.unit !== 0
          ? { ...asset, todo: asset.unit > 0 ? 'SELL' : 'BUY' }
          : []
      ),
    [] as (IStrategyAsset & { todo: 'SELL' | 'BUY' })[]
  );

export const slugify = (str: string) => {
  return str
    .replace(/ /g, '_')
    .replace(/[^0-9a-z_-]/gi, '')
    .toLowerCase();
};

export const envVar = (key: string) => {
  return process.env[`REACT_APP_${key}`] || '';
};

export const getMarker = ({
  axis = 'x',
  date,
  lineColor = '#FF2B00',
}: {
  axis?: 'x' | 'y';
  date: string;
  lineColor?: string;
}) => ({
  axis,
  lineStyle: {
    stroke: lineColor,
    strokeWidth: 1.25,
    strokeDasharray: 2.5,
  },
  value: new Date(date).getTime(),
});

export const getStartEndDates = (interval: (typeof INTERVALS)[number]) => {
  const timePeriod =
    interval === 'VAR_24H'
      ? 'day'
      : interval === 'VAR_7D'
      ? 'week'
      : interval === 'VAR_30D'
      ? 'month'
      : 'year';

  const endDate = moment().toDate();
  const startDate = moment().subtract(1, timePeriod).toDate();

  return { endDate, startDate };
};

export const getFormattedValue = (
  value: number | null,
  suffix: string = '',
  threshold: NumberAbbreviationEnum = NumberAbbreviationEnum.B,
  precision = 2
) => {
  const thresholds = {
    K: 10 ** 3,
    M: 10 ** 6,
    B: 10 ** 9,
    T: 10 ** 12,
  };

  const currentThreshold = thresholds[threshold];

  let formattedValue = value;

  if (typeof value === 'number' && formattedValue) {
    const absValue = Math.abs(value);
    if (absValue >= thresholds['T'] && currentThreshold <= thresholds['T']) {
      formattedValue /= thresholds['T'];
      suffix = 'T' + suffix;
    } else if (
      absValue >= thresholds['B'] &&
      currentThreshold <= thresholds['B']
    ) {
      formattedValue /= thresholds['B'];
      suffix = 'B' + suffix;
    } else if (
      absValue >= thresholds['M'] &&
      currentThreshold <= thresholds['M']
    ) {
      formattedValue /= thresholds['M'];
      suffix = 'M' + suffix;
    } else if (
      absValue >= thresholds['K'] &&
      currentThreshold <= thresholds['K']
    ) {
      formattedValue /= thresholds['K'];
      suffix = 'K' + suffix;
    }

    formattedValue = +formattedValue.toFixed(precision);
  }

  return { formattedValue, suffix };
};

export const getMarkersAndTickers = (
  data: LineChartData[],
  events: Event[]
) => {
  const endDate = moment(data[0].data.slice(-1)[0].x);
  const startDate = moment(data[0].data[0].x);
  const diffDays = endDate.diff(startDate, 'days');

  const markers = events
    .filter(
      ({ startDate: date }) =>
        date.localeCompare(startDate.format('YYYY-MM-DD')) > -1 &&
        date.localeCompare(endDate.format('YYYY-MM-DD')) < 1
    )
    .map(({ startDate: date, title }) => ({
      ...getMarker({ date }),
      date,
      title,
    }));

  const tickValues =
    diffDays <= 20
      ? 'every day'
      : diffDays < 60
      ? 'every 2 days'
      : 'every month';

  return { markers, tickValues };
};

/**
 *
 * @param strategy
 */
export const calcStrategyValues = (strategy: IStrategy) => {
  const historyCurrency =
    strategy.currency.toLocaleLowerCase() as TTickerCurrency;

  return strategy.strategyAssets.items.reduce(
    (acc, { unit, entryPrice, asset }, i, arr) => {
      const lastPrice = asset.history[0][historyCurrency] || 0;
      const priceVariation = lastPrice - entryPrice;
      acc.initialValue = acc.initialValue + unit * entryPrice;
      acc.variation = acc.variation + unit * priceVariation;
      if (i === arr.length - 1) {
        acc.variationPercentage = computePercentage(
          acc.initialValue,
          acc.variation
        );
      }
      acc.currentValue = acc.currentValue + unit * lastPrice;
      return acc;
    },
    { initialValue: 0, currentValue: 0, variation: 0, variationPercentage: 0 }
  );
};

export const numberWithCommas = (x: any) => {
  const parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

export const cleanNumber = (x: unknown) => {
  let val = 0;
  if (typeof x === 'number') {
    val = x;
  }
  if (typeof x === 'string') {
    val = +x.replace(/[^\d.-]/g, '');
  }
  return Number.isNaN(val) || !Number.isFinite(val) ? 0 : val;
};

export const nearestNumber = (arr: number[], num: number): number => {
  return arr.reduce((prev, curr) =>
    Math.abs(curr - num) < Math.abs(prev - num) ? curr : prev
  );
};

export const calculateWeightedAverage = (assets: IStrategyAsset[]): number => {
  let totalWeightedPrice: number = 0;
  let totalUnits: number = 0;

  assets.forEach((asset) => {
    totalWeightedPrice += asset.entryPrice * asset.unit;
    totalUnits += asset.unit;
  });

  if (totalUnits === 0) {
    return 0;
  }

  const weightedAverage: number = totalWeightedPrice / totalUnits;
  return weightedAverage;
};
